import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  InputAdornment,
  Button,
  Grow,
  MenuItem,
  Select,
  Autocomplete,
  Tooltip,
  Checkbox,
  Link,
} from '@mui/material'
import {
  Search as SearchIcon,
  Sync as SyncIcon,
  FileDownload as FileDownloadIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
} from '@mui/icons-material'
import { format, parse } from 'date-fns'
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import AdvancedPagination from '@/components/pagination/AdvancedPagination'
import { useDebounce } from '@/hooks/useDebounce'
import {
  getInvoices,
  exportInvoicesCSV,
  Invoice,
  InvoiceParams,
  getContractDropdowns,
  getServiceDropdowns,
  ContractDropdownOption,
  ServiceDropdownOption,
  ContractId,
} from '@/services/invoicesService'
import InvoiceDialog from '@/components/contracts/InvoiceDialog'
import ClaimDialog from '@/components/contracts/ClaimDialog'
import api from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import {
  useAgencyDirectory,
  getDirectoryOptionLabel,
} from '@/hooks/useAgencyDirectory'
import { ExternalLink } from 'lucide-react'
import CreatePaymentBatchModal, {
  CreatePaymentBatchPayload,
} from '../AgencyPaymentBatches/components/CreatePaymentBatchModal'
import {
  BatchInvoice,
  formatCurrency,
} from '../AgencyPaymentBatches/mockData'
import { createBatch } from '@/services/batchService'

// Helper function to format payment method for display
const formatPaymentMethod = (
  paymentMethod: string | undefined | null
): string => {
  if (!paymentMethod) return 'N/A'

  // Convert snake_case or kebab-case to Title Case
  return paymentMethod
    .split(/[_-]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Small stat pill shown in the payment-batch selection toolbar
const SelectionStat: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      px: 2,
      py: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
)

const AgencyInvoicePage: React.FC = () => {
  const { isPatient, hasPermission } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [totalRecords, setTotalRecords] = useState<number>(0)
  const [totalProviderPayout, setTotalProviderPayout] = useState<number>(0)

  // Summary cards data (placeholder until backend provides)
  const [summaryData, setSummaryData] = useState({
    totalInvoicedAmount: 0,
    totalPaidAmount: 0,
    totalPendingPayment: 0,
  })

  // Filters - Initialize search from location state if provided
  const [search, setSearch] = useState<string>(
    (location.state as any)?.searchQuery || ''
  )
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [contractId, setContractId] = useState<string>('')
  const [serviceId, setServiceId] = useState<string>('')
  const [filterPatientId, setFilterPatientId] = useState<string>('')
  const [filterProviderId, setFilterProviderId] = useState<string>('')
  // '' = All, 'pending' = isPayoutCreated false, 'added' = isPayoutCreated true
  const [payoutStatusFilter, setPayoutStatusFilter] = useState<string>('')

  const { patients, providers } = useAgencyDirectory()

  // Dropdown options
  const [contractOptions, setContractOptions] = useState<
    ContractDropdownOption[]
  >([])
  const [serviceOptions, setServiceOptions] = useState<ServiceDropdownOption[]>(
    []
  )
  const [loadingDropdowns, setLoadingDropdowns] = useState(false)
  const [selectedContract, setSelectedContract] =
    useState<ContractDropdownOption | null>(null)
  const [selectedService, setSelectedService] =
    useState<ServiceDropdownOption | null>(null)
  const [contractSearch, setContractSearch] = useState<string>('')

  // Pagination
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)

  // Invoice Dialog state
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
  const [invoiceDialogData, setInvoiceDialogData] = useState<any>(null)
  const [loadingInvoice, setLoadingInvoice] = useState(false)
  const [invoiceDialogReadOnly, setInvoiceDialogReadOnly] = useState(false)

  // Claim Dialog state
  const [claimDialogOpen, setClaimDialogOpen] = useState(false)
  const [claimDialogData, setClaimDialogData] = useState<any>(null)
  const [loadingClaim] = useState(false)

  // Payment batch selection (mock-only until backend is ready)
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(
    new Set()
  )
  const [createBatchOpen, setCreateBatchOpen] = useState(false)

  const debouncedSearch = useDebounce(search, 500)

  // Fetch dropdown options
  useEffect(() => {
    const fetchDropdowns = async () => {
      setLoadingDropdowns(true)
      try {
        const [contracts, services] = await Promise.all([
          getContractDropdowns(),
          getServiceDropdowns(),
        ])
        setContractOptions(contracts)
        setServiceOptions(services)
      } catch (err) {
        console.error('Error fetching dropdowns:', err)
      } finally {
        setLoadingDropdowns(false)
      }
    }
    fetchDropdowns()
  }, [])

  // Fetch contracts when search changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const fetchContracts = async () => {
        setLoadingDropdowns(true)
        try {
          const contracts = await getContractDropdowns(contractSearch)
          setContractOptions(contracts)
        } catch (err) {
          console.error('Error fetching contracts:', err)
        } finally {
          setLoadingDropdowns(false)
        }
      }
      if (contractSearch) {
        fetchContracts()
      } else {
        // Reset to initial list when search is cleared
        const fetchDropdowns = async () => {
          setLoadingDropdowns(true)
          try {
            const contracts = await getContractDropdowns()
            setContractOptions(contracts)
          } catch (err) {
            console.error('Error fetching dropdowns:', err)
          } finally {
            setLoadingDropdowns(false)
          }
        }
        fetchDropdowns()
      }
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [contractSearch])

  const fetchInvoices = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Convert dates to YYYY-MM-DD format for API
      const formatDateForAPI = (date: Date | null): string | undefined => {
        if (!date) return undefined
        return format(date, 'yyyy-MM-dd')
      }

      const params: InvoiceParams = {
        search: debouncedSearch || undefined,
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
        contractId: contractId || undefined,
        // serviceId: serviceId || undefined,
        patientId: filterPatientId || undefined,
        therapistId: filterProviderId || undefined,
        payoutStatus:
          payoutStatusFilter === 'added'
            ? true
            : payoutStatusFilter === 'pending'
              ? false
              : undefined,
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
      }

      const response = await getInvoices(params)
      const invoicesList = response.invoices || response.data || []
      setInvoices(invoicesList)
      setTotalRecords(response.totalRecords || invoicesList.length)
      setTotalProviderPayout(
        (response as any).totalProviderPayout ??
        invoicesList.reduce((sum, inv) => sum + (inv.providerPayout || 0), 0)
      )

      // Use metadata from API response
      setSummaryData({
        totalInvoicedAmount:
          response.totalInvoiceAmount ??
          response.totalInvoicedAmount ??
          invoicesList.reduce((sum, inv) => sum + (inv.amount || 0), 0),
        totalPaidAmount:
          response.totalPaidAmount ??
          invoicesList
            .filter(inv => inv.status === 'Paid')
            .reduce((sum, inv) => sum + (inv.amount || 0), 0),
        totalPendingPayment:
          response.totalPendingAmount ??
          response.totalPendingPayment ??
          invoicesList
            .filter(inv => inv.status === 'Pending' || inv.status === 'Draft')
            .reduce((sum, inv) => sum + (inv.amount || 0), 0),
      })
    } catch (err: any) {
      console.error('Error fetching invoices:', err)
      setError(err?.response?.data?.message || 'Failed to fetch invoices')
      setInvoices([])
      setTotalRecords(0)
      setTotalProviderPayout(0)
    } finally {
      setLoading(false)
    }
  }, [
    debouncedSearch,
    startDate,
    endDate,
    contractId,
    // serviceId,
    filterPatientId,
    filterProviderId,
    payoutStatusFilter,
    page,
    rowsPerPage,
  ])

  useEffect(() => {
    fetchInvoices()
  }, [fetchInvoices])

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleExportCSV = async () => {
    try {
      setLoading(true)

      // Convert dates to YYYY-MM-DD format for API
      const formatDateForAPI = (date: Date | null): string | undefined => {
        if (!date) return undefined
        return format(date, 'yyyy-MM-dd')
      }

      const params: Omit<InvoiceParams, 'page' | 'limit'> = {
        search: debouncedSearch || undefined,
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
        contractId: contractId || undefined,
        // serviceId: serviceId || undefined,
        patientId: filterPatientId || undefined,
        therapistId: filterProviderId || undefined,
      }

      const blob = await exportInvoicesCSV(params)

      // Create download link
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute(
        'download',
        `invoices-${format(new Date(), 'yyyy-MM-dd')}.csv`
      )
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err: any) {
      console.error('Error exporting invoices:', err)
      setError(err?.response?.data?.message || 'Failed to export invoices')
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilters = () => {
    setSearch('')
    setStartDate(null)
    setEndDate(null)
    setContractId('')
    // setServiceId('')
    setSelectedContract(null)
    setSelectedService(null)
    setContractSearch('')
    setFilterPatientId('')
    setFilterProviderId('')
    setPayoutStatusFilter('')
    setPage(0)
  }

  const hasActiveFilters = Boolean(
    search ||
    startDate ||
    endDate ||
    contractId ||
    // serviceId ||
    selectedContract ||
    selectedService ||
    filterPatientId ||
    filterProviderId ||
    payoutStatusFilter
  )

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      return format(new Date(dateString), 'MM/dd/yyyy')
    } catch {
      return dateString
    }
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'Paid':
        return { color: 'success', label: 'Paid' }
      case 'Pending':
        return { color: 'warning', label: 'Pending' }
      case 'Draft':
        return { color: 'default', label: 'Draft' }
      case 'Overdue':
        return { color: 'error', label: 'Overdue' }
      default:
        return { color: 'default', label: status }
    }
  }

  const getContractId = (invoice: Invoice): string => {
    if (
      typeof invoice.contract_id === 'object' &&
      invoice.contract_id?.contract_ids
    ) {
      return invoice.contract_id.contract_ids
    }
    if (typeof invoice.contract_id === 'string') {
      return invoice.contract_id
    }
    if (invoice.contract_ids) {
      return invoice.contract_ids
    }
    return 'N/A'
  }

  const getProviderName = (invoice: Invoice): string => {
    // Check for provider_name field
    if ((invoice as any).provider_name) {
      return (invoice as any).provider_name
    }

    // Check if therapistId exists in invoice data
    if ((invoice as any).therapistId) {
      const therapistId = (invoice as any).therapistId
      if (
        typeof therapistId === 'object' &&
        therapistId.firstName &&
        therapistId.lastName
      ) {
        return `${therapistId.firstName} ${therapistId.lastName}`
      }
    }

    if ((invoice as any).claim_id) {
      return `${(invoice as any).claim_id.rProvFistName || ''} ${(invoice as any).claim_id.rProvLastName || ''}`
    }

    // Check for therapist name in contract_id object
    if (
      typeof invoice.contract_id === 'object' &&
      (invoice.contract_id as any).therapist_name
    ) {
      return (invoice.contract_id as any).therapist_name
    }
    return 'N/A'
  }

  const getSessionId = (invoice: Invoice): string | null => {
    return (
      invoice.sessionId || invoice.session_id || invoice.session_ids || null
    )
  }

  const handleView = async (invoiceId: string) => {
    setLoadingInvoice(true)
    setInvoiceDialogReadOnly(true)
    setInvoiceDialogOpen(true)

    try {
      // Fetch invoice details - using session_id from invoice if available
      const invoice = invoices.find(inv => inv._id === invoiceId)
      const sessionId = getSessionId(invoice || ({} as Invoice)) || invoiceId

      // Try invoice endpoint first, fallback to invoice-detail with session_id
      let response
      try {
        response = await api.get(`/invoices/${invoiceId}`)
      } catch {
        // Fallback to invoice-detail endpoint using session_id
        response = await api.get(`/invoices/invoice-detail/${sessionId}`)
      }

      if (response.data) {
        setInvoiceDialogData(response.data)
      }
    } catch (err: any) {
      console.error('Error fetching invoice details:', err)
      setError('Failed to load invoice details. Please try again.')
      setInvoiceDialogOpen(false)
    } finally {
      setLoadingInvoice(false)
    }
  }

  const handleEdit = async (invoiceId: string) => {
    setLoadingInvoice(true)
    setInvoiceDialogReadOnly(false)
    setInvoiceDialogOpen(true)

    try {
      // Fetch invoice details - using session_id from invoice if available
      const invoice = invoices.find(inv => inv._id === invoiceId)
      const sessionId = getSessionId(invoice || ({} as Invoice)) || invoiceId

      // Try invoice endpoint first, fallback to invoice-detail with session_id
      let response
      try {
        response = await api.get(`/invoices/${invoiceId}`)
      } catch {
        // Fallback to invoice-detail endpoint using session_id
        response = await api.get(`/invoices/invoice-detail/${sessionId}`)
      }

      if (response.data) {
        setInvoiceDialogData(response.data)
      }
    } catch (err: any) {
      console.error('Error fetching invoice details:', err)
      setError('Failed to load invoice details. Please try again.')
      setInvoiceDialogOpen(false)
    } finally {
      setLoadingInvoice(false)
    }
  }

  const handleDownload = (invoiceId: string) => {
    const invoice = invoices.find(inv => inv._id === invoiceId)
    if (invoice?.invoice_url) {
      window.open(invoice.invoice_url, '_blank')
    } else {
      setError('Invoice download URL not available')
    }
  }

  const getClaimId = (invoice: Invoice): string | null => {
    if (!invoice.claim_id) return null
    if (typeof invoice.claim_id === 'object') {
      return invoice.claim_id.claimId || invoice.claim_id._id || null
    }
    return invoice.claim_id
  }

  // --- Provider-centric display + payment-batch selection (mock fallback) ---
  const normalizeInvoice = (invoice: Invoice): BatchInvoice => ({
    _id: invoice._id,
    invoice_ids: invoice.invoice_ids || 'N/A',
    contract_ids: getContractId(invoice),
    session_ids: getSessionId(invoice) || 'N/A',
    patient_name: invoice.patient_name || 'N/A',
    service_name: invoice.service_name || 'N/A',
    totalAmount: (invoice as any).totalAmount ?? invoice.amount ?? 0,
    providerPayout: (invoice as any).providerPayout ?? 0,
    payment_date: invoice.payment_date,
    payment_method: invoice.payment_method || 'N/A',
    payment_for: invoice.payment_for || 'N/A',
    claim_id: getClaimId(invoice) || undefined,
    status: invoice.status,
    provider: {
      _id:
        (invoice as any).therapistId?._id ||
        (invoice as any).therapistId ||
        invoice._id,
      name: getProviderName(invoice),
      role: (invoice as any).provider_role || '',
    },
  })

  // Selected invoices mapped into the batch shape (for the toolbar + modal).
  const selectedRows: BatchInvoice[] = invoices
    .filter(inv => selectedInvoiceIds.has(inv._id))
    .map(normalizeInvoice)
  const selectionProviderCount = new Set(
    selectedRows.map(row => row.provider._id)
  ).size
  const selectionTotal = selectedRows.reduce(
    (sum, row) => sum + (row.totalAmount || 0),
    0
  )
  const selectionProviderPayout = selectedRows.reduce(
    (sum, row) => sum + (row.providerPayout || 0),
    0
  )
  // Invoices already added to a payout batch cannot be selected again.
  const selectableInvoices = invoices.filter(inv => !inv.isPayoutCreated)
  const allSelected =
    selectableInvoices.length > 0 &&
    selectableInvoices.every(inv => selectedInvoiceIds.has(inv._id))

  const toggleSelectAll = () => {
    setSelectedInvoiceIds(
      allSelected
        ? new Set()
        : new Set(selectableInvoices.map(inv => inv._id))
    )
  }

  const toggleSelectOne = (id: string) => {
    setSelectedInvoiceIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleCreateBatch = async (payload: CreatePaymentBatchPayload) => {
    try {
      const providerIds = new Set(selectedRows.map(row => row.provider._id))
      await createBatch({
        batchName: payload.name,
        batchMonth: format(
          parse(payload.month, 'MMMM yyyy', new Date()),
          'yyyy-MM'
        ),
        description: payload.description || undefined,
        totalProvider: providerIds.size,
        totalInvoice: selectedRows.length,
        totalAmount: selectionTotal,
        providerPayout: selectionProviderPayout,
        totalClaim: 0,
        batchType: 'invoice',
        invoiceIds: selectedRows.map(row => row._id),
      })
      setCreateBatchOpen(false)
      setSelectedInvoiceIds(new Set())
      navigate('/agency-payment-batches')
    } catch (err: any) {
      setError(
        err?.response?.data?.message || 'Failed to create payout batch'
      )
    }
  }

  // Commented out - Now redirecting to claims page with search query instead of opening dialog
  // const handleViewClaim = async (claimId: string) => {
  //   setLoadingClaim(true)
  //   setClaimDialogOpen(true)

  //   try {
  //     // Find invoice by claim_id (handle both object and string formats)
  //     const invoice = invoices.find(inv => {
  //       const invClaimId = getClaimId(inv)
  //       return invClaimId === claimId
  //     })

  //     // Get sessionId from claim_id object, invoice sessionId, or fallback to claimId
  //     const sessionId =
  //       getClaimSessionId(invoice || ({} as Invoice)) ||
  //       invoice?.sessionId ||
  //       invoice?.session_id ||
  //       invoice?.session_ids ||
  //       claimId

  //     // Fetch full claim details
  //     const response = await api.get(
  //       `/insurance-claims/claim-detail/${sessionId}`
  //     )
  //     if (response.data) {
  //       setClaimDialogData(response.data)
  //     } else {
  //       setError('Failed to load claim details. Please try again.')
  //       setClaimDialogOpen(false)
  //     }
  //   } catch (err: any) {
  //     console.error('Error fetching claim details:', err)
  //     setError('Failed to load claim details. Please try again.')
  //     setClaimDialogOpen(false)
  //   } finally {
  //     setLoadingClaim(false)
  //   }
  // }

  const handleGenerateInvoice = async (payload: {
    session_id: string
    bill_address: string
    bill_city: string
    bill_state: string
    bill_zip: string
    discount: number
    amount: number
  }) => {
    try {
      // Call the generate invoice API to update the invoice
      const response = await api.post('/invoices/generate', payload)

      if (response.data) {
        // Close dialog and refresh invoices list
        setInvoiceDialogOpen(false)
        await fetchInvoices()
      }
    } catch (err: any) {
      console.error('Error updating invoice:', err)
      setError(
        err.response?.data?.message ||
        'Failed to update invoice. Please try again.'
      )
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ color: 'primary.main', fontWeight: 600 }}
            gutterBottom
          >
            Invoices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage invoices
          </Typography>
        </Box>
        {!isPatient && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCSV}
              disabled={loading || invoices.length === 0}
              sx={{
                textTransform: 'none',
                borderRadius: '0.5rem',
                fontWeight: 500,
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              Export CSV
            </Button>
          </Box>
        )}
      </Box>

      {/* Summary Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2.5,
          mb: 3,
        }}
      >
        {/* Total Invoiced Amount Card */}
        <Card
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '1rem',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(102, 126, 234, 0.4)',
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
            }}
          />
          <CardContent sx={{ position: 'relative', p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  Total Invoiced Amount
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  }}
                >
                  ${summaryData.totalInvoicedAmount?.toFixed(2)}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ReceiptIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Total Paid Amount Card */}
        <Card
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '1rem',
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            boxShadow: '0 4px 20px rgba(17, 153, 142, 0.3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(17, 153, 142, 0.4)',
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
            }}
          />
          <CardContent sx={{ position: 'relative', p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  Total Paid Amount
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  }}
                >
                  ${summaryData.totalPaidAmount?.toFixed(2)}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircleIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Total Pending Payment Card */}
        <Card
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '1rem',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            boxShadow: '0 4px 20px rgba(245, 87, 108, 0.3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(245, 87, 108, 0.4)',
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
            }}
          />
          <CardContent sx={{ position: 'relative', p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  Total Pending Payment
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  }}
                >
                  ${summaryData.totalPendingPayment?.toFixed(2)}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PendingIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Provider Payout Card */}
        <Card
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '1rem',
            background: 'linear-gradient(135deg, #b19ffb 0%, #764ba2 100%)',
            boxShadow: '0 4px 20px rgba(118, 75, 162, 0.3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(118, 75, 162, 0.4)',
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
            }}
          />
          <CardContent sx={{ position: 'relative', p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  Provider Payout
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  }}
                >
                  ${totalProviderPayout.toFixed(2)}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AccountBalanceWalletIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters Card */}
      <Card
        sx={{
          mb: 3,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          borderRadius: '1rem',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 3.5 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
              {/* Main Filters Row */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'flex-end',
                }}
              >
                {/* Search Filter - Takes more space */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(40% - 8px)',
                      lg: '2 1 200px',
                    },
                    minWidth: { xs: '100%', sm: '200px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Search
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Search by Invoice ID, Session ID or Patient..."
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ mr: 0.5 }}>
                          <SearchIcon
                            fontSize="small"
                            sx={{ color: 'text.secondary', fontSize: '18px' }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.625rem',
                        backgroundColor: 'grey.50',
                        fontSize: '0.875rem',
                        '&:hover': {
                          backgroundColor: 'grey.100',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'background.paper',
                        },
                        '& fieldset': {
                          borderColor: 'grey.300',
                        },
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: '1.5px',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Start Date Filter */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(30% - 8px)',
                      lg: '1 1 180px',
                    },
                    minWidth: { xs: '100%', sm: '150px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Start Date
                  </Typography>
                  <DatePicker
                    value={startDate}
                    onChange={(newValue: Date | null) => setStartDate(newValue)}
                    format="MM/dd/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        placeholder: 'MM/DD/YYYY',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0.625rem',
                            backgroundColor: 'grey.50',
                            fontSize: '0.875rem',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'background.paper',
                            },
                            '& fieldset': {
                              borderColor: 'grey.300',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: '1.5px',
                            },
                          },
                        },
                      },
                    }}
                  />
                </Box>

                {/* End Date Filter */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(30% - 8px)',
                      lg: '1 1 180px',
                    },
                    minWidth: { xs: '100%', sm: '150px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    End Date
                  </Typography>
                  <DatePicker
                    value={endDate}
                    onChange={(newValue: Date | null) => setEndDate(newValue)}
                    format="MM/dd/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        placeholder: 'MM/DD/YYYY',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0.625rem',
                            backgroundColor: 'grey.50',
                            fontSize: '0.875rem',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'background.paper',
                            },
                            '& fieldset': {
                              borderColor: 'grey.300',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: '1.5px',
                            },
                          },
                        },
                      },
                    }}
                  />
                </Box>

                {/* Contract ID Filter */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(30% - 8px)',
                      lg: '1 1 180px',
                    },
                    minWidth: { xs: '100%', sm: '180px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Contract Id
                  </Typography>
                  <Autocomplete
                    options={contractOptions}
                    getOptionLabel={option => option.contract_ids || ''}
                    value={selectedContract}
                    onChange={(event, newValue) => {
                      setSelectedContract(newValue)
                      setContractId(newValue?._id || '')
                    }}
                    onInputChange={(event, newInputValue) => {
                      setContractSearch(newInputValue)
                    }}
                    loading={loadingDropdowns}
                    size="small"
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Select Contract ID"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                <PersonIcon
                                  fontSize="small"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '18px',
                                  }}
                                />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0.625rem',
                            backgroundColor: 'grey.50',
                            fontSize: '0.875rem',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'background.paper',
                            },
                            '& fieldset': {
                              borderColor: 'grey.300',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: '1.5px',
                            },
                          },
                        }}
                      />
                    )}
                    slotProps={{
                      popper: { sx: { zIndex: 9999 } },
                    }}
                  />
                </Box>

                {/* Service Filter */}
                {/* <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(30% - 8px)',
                      lg: '1 1 180px',
                    },
                    minWidth: { xs: '100%', sm: '180px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Service
                  </Typography>
                  <Autocomplete
                    options={serviceOptions}
                    getOptionLabel={option => option.name || ''}
                    value={selectedService}
                    onChange={(event, newValue) => {
                      setSelectedService(newValue)
                      setServiceId(newValue?._id || '')
                    }}
                    loading={loadingDropdowns}
                    size="small"
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Select Service"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                <SettingsIcon
                                  fontSize="small"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '18px',
                                  }}
                                />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0.625rem',
                            backgroundColor: 'grey.50',
                            fontSize: '0.875rem',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'background.paper',
                            },
                            '& fieldset': {
                              borderColor: 'grey.300',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: '1.5px',
                            },
                          },
                        }}
                      />
                    )}
                    slotProps={{
                      popper: { sx: { zIndex: 9999 } },
                    }}
                  />
                </Box> */}

                {/* Patient Filter */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(30% - 8px)',
                      lg: '1 1 180px',
                    },
                    minWidth: { xs: '100%', sm: '180px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Patient
                  </Typography>
                  <Select
                    value={filterPatientId}
                    onChange={event => setFilterPatientId(event.target.value as string)}
                    displayEmpty
                    size="small"
                    fullWidth
                    sx={{
                      borderRadius: '0.625rem',
                      backgroundColor: 'grey.50',
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'background.paper',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.300',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: '1.5px',
                      },
                    }}
                  >
                    <MenuItem value="">All Patients</MenuItem>
                    {patients.map(patient => (
                      <MenuItem key={patient._id} value={patient._id}>
                        {getDirectoryOptionLabel(patient)}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                {/* Provider Filter */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(30% - 8px)',
                      lg: '1 1 180px',
                    },
                    minWidth: { xs: '100%', sm: '180px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Provider
                  </Typography>
                  <Select
                    value={filterProviderId}
                    onChange={event => setFilterProviderId(event.target.value as string)}
                    displayEmpty
                    size="small"
                    fullWidth
                    sx={{
                      borderRadius: '0.625rem',
                      backgroundColor: 'grey.50',
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'background.paper',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.300',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: '1.5px',
                      },
                    }}
                  >
                    <MenuItem value="">All Providers</MenuItem>
                    {providers.map(provider => (
                      <MenuItem key={provider._id} value={provider._id}>
                        {getDirectoryOptionLabel(provider)}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                {/* Payout Status Filter */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1 1 calc(50% - 8px)',
                      md: '1 1 calc(30% - 8px)',
                      lg: '1 1 180px',
                    },
                    minWidth: { xs: '100%', sm: '180px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.25,
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '0.8125rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Payout Status
                  </Typography>
                  <Select
                    value={payoutStatusFilter}
                    onChange={event =>
                      setPayoutStatusFilter(event.target.value as string)
                    }
                    displayEmpty
                    size="small"
                    fullWidth
                    sx={{
                      borderRadius: '0.625rem',
                      backgroundColor: 'grey.50',
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'background.paper',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.300',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: '1.5px',
                      },
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending Payout</MenuItem>
                    <MenuItem value="added">Added to Payout</MenuItem>
                  </Select>
                </Box>

                {/* Reset Filter Button */}
                <Box
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '0 0 auto',
                    },
                    minWidth: { xs: '100%', sm: 'auto' },
                    display: 'flex',
                    alignItems: 'flex-end',
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<SyncIcon sx={{ fontSize: '18px' }} />}
                    onClick={handleClearFilters}
                    size="small"
                    sx={{
                      textTransform: 'none',
                      borderRadius: '0.625rem',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      fontWeight: 500,
                      fontSize: '0.8125rem',
                      px: 2.5,
                      py: 1,
                      height: '40px',
                      minWidth: '100px',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* Selection toolbar for payment batch creation */}
      {!isPatient && selectedRows.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            gap: 2,
            mb: 3,
            p: 2,
            borderRadius: '0.75rem',
            bgcolor: '#f3f0fb',
            border: '1px solid #ddd4f0',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'nowrap' }}>
            <SelectionStat
              label="Selected Providers"
              value={String(selectionProviderCount)}
            />
            <SelectionStat
              label="Selected Invoices"
              value={String(selectedRows.length)}
            />
            <SelectionStat
              label="Total Amount"
              value={formatCurrency(selectionTotal)}
            />
            <SelectionStat
              label="Provider Payout"
              value={formatCurrency(selectionProviderPayout)}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button
              variant="text"
              startIcon={<CloseIcon />}
              onClick={() => setSelectedInvoiceIds(new Set())}
              sx={{ textTransform: 'none', color: '#475569', whiteSpace: 'nowrap' }}
            >
              Clear Selection
            </Button>
            <Button
              variant="contained"
              startIcon={<AssignmentIcon />}
              onClick={() => {
                setError(null)
                setCreateBatchOpen(true)
              }}
              sx={{
                textTransform: 'none',
                borderRadius: '0.5rem',
                bgcolor: '#7A6FA1',
                '&:hover': { bgcolor: '#655a8a' },
              }}
            >
              Create Payment Batch
            </Button>
          </Box>
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table Card */}
      <Grow in timeout={300}>
        <Card sx={{ boxShadow: 2 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 400,
                }}
              >
                <CircularProgress />
              </Box>
            ) : invoices.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 400,
                  gap: 2,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  No invoices found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  {hasActiveFilters
                    ? 'Try adjusting or clearing your filters to see more results.'
                    : 'Invoices will appear here once they are created.'}
                </Typography>
              </Box>
            ) : (
              <>
                <Table
                  sx={{
                    minWidth: 1500,
                    '& th, & td': { whiteSpace: 'nowrap' },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main' }}>
                      {!isPatient && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={allSelected}
                            indeterminate={
                              selectedRows.length > 0 && !allSelected
                            }
                            onChange={toggleSelectAll}
                            sx={{
                              color: 'white',
                              '&.Mui-checked': { color: 'white' },
                              '&.MuiCheckbox-indeterminate': { color: 'white' },
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Invoice ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Contract ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Session ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        {isPatient ? 'Provider Name' : 'Patient Name'}
                      </TableCell>
                      {!isPatient && (
                        <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                          Provider Name
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Service
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Total Amount
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Discount
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Paid Amount
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Provider Payout
                      </TableCell>
                      {!isPatient && (
                        <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                          Platform Fee
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Payment Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Payment Method
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Payment For
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                        Status
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: 'white',
                          textAlign: 'center',
                        }}
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice, index) => {
                      const statusConfig = getStatusConfig(invoice.status)
                      const contractIdValue = getContractId(invoice)
                      const contractMongoId =
                        (invoice.contract_id as ContractId)?._id || ''
                      const isSelected = selectedInvoiceIds.has(invoice._id)

                      return (
                        <Grow
                          in
                          key={invoice._id}
                          timeout={200 + index * 50}
                          style={{ transformOrigin: '0 0 0' }}
                        >
                          <TableRow
                            hover
                            selected={isSelected}
                            sx={{
                              cursor: 'default',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            {!isPatient && (
                              <TableCell padding="checkbox">
                                <Tooltip
                                  title={
                                    invoice.isPayoutCreated
                                      ? 'Already added to a payout batch'
                                      : ''
                                  }
                                  placement="top"
                                >
                                  <span>
                                    <Checkbox
                                      checked={isSelected}
                                      disabled={Boolean(invoice.isPayoutCreated)}
                                      onChange={() =>
                                        toggleSelectOne(invoice._id)
                                      }
                                    />
                                  </span>
                                </Tooltip>
                              </TableCell>
                            )}
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {invoice.invoice_ids || 'N/A'}
                              </Typography>
                              {!isPatient && invoice.isPayoutCreated && (
                                <Chip
                                  label="Added to Payout"
                                  size="small"
                                  sx={{
                                    mt: 0.5,
                                    height: 20,
                                    fontSize: '0.6875rem',
                                    fontWeight: 500,
                                    bgcolor: '#dcfce7',
                                    color: '#166534',
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Link
                                component={RouterLink}
                                to={
                                  isPatient
                                    ? `/contract-details/${contractMongoId}`
                                    : `/therapy-contracts/${contractMongoId}`
                                }
                                sx={{
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  fontWeight: 600,
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    color: 'primary.dark',
                                  },
                                }}
                              >
                                {contractIdValue}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {invoice.session_ids || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {isPatient
                                  ? getProviderName(invoice)
                                  : invoice.patient_name || 'N/A'}
                              </Typography>
                            </TableCell>
                            {!isPatient && (
                              <TableCell>
                                <Typography variant="body2">
                                  {getProviderName(invoice)}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell>
                              <Typography variant="body2">
                                {invoice.service_name || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                ${invoice.totalAmount?.toFixed(2) || '0.00'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                ${invoice.discount?.toFixed(2) || '0.00'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                ${invoice.paid_amount?.toFixed(2) || '0.00'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                ${invoice.providerPayout?.toFixed(2) || '0.00'}
                              </Typography>
                            </TableCell>
                            {!isPatient && (
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  $
                                  {invoice.commissionAmount?.toFixed(2) ||
                                    '0.00'}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(invoice.payment_date)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatPaymentMethod(invoice.payment_method)}
                                {getClaimId(invoice) && (
                                  <>
                                    <br />
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        mt: 0.5,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        component="span"
                                        onClick={() =>
                                          navigate('/claims', {
                                            state: {
                                              searchQuery:
                                                getClaimId(invoice) || '',
                                            },
                                          })
                                        }
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          textDecoration: 'underline',
                                          cursor: 'pointer',
                                          color: 'primary.main',
                                          whiteSpace: 'nowrap',
                                          '&:hover': {
                                            color: 'primary.dark',
                                          },
                                        }}
                                      >
                                        Claim ID: {getClaimId(invoice)}
                                        <ExternalLink
                                          className="w-3 h-3"
                                          style={{ color: 'primary.main' }}
                                        />
                                      </Typography>
                                    </Box>
                                  </>
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {invoice.payment_for || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={statusConfig.label}
                                color={statusConfig.color as any}
                                size="small"
                                sx={{
                                  fontWeight: 500,
                                  borderRadius: '1rem',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  gap: 1,
                                }}
                              >
                                {(invoice.status !== 'Draft' || isPatient) && (
                                  <Tooltip placement="top" title="View Invoice">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleView(invoice._id)}
                                      sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                          backgroundColor: 'action.hover',
                                        },
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {invoice.status === 'Draft' &&
                                  !isPatient &&
                                  hasPermission('invoice.edit') && (
                                    <Tooltip
                                      placement="top"
                                      title="Edit Invoice"
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEdit(invoice._id)}
                                        sx={{
                                          color: 'primary.main',
                                          '&:hover': {
                                            backgroundColor: 'action.hover',
                                          },
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                {invoice.status === 'Paid' && (
                                  <Tooltip
                                    placement="top"
                                    title="Download Invoice"
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleDownload(invoice._id)
                                      }
                                      sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                          backgroundColor: 'action.hover',
                                        },
                                      }}
                                    >
                                      <FileDownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        </Grow>
                      )
                    })}
                  </TableBody>
                </Table>
              </>
            )}
          </TableContainer>

          {/* Pagination — outside the scroll container so it stays fixed on horizontal scroll */}
          {!loading && invoices.length > 0 && totalRecords > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
              <AdvancedPagination
                count={totalRecords}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Box>
          )}
        </Card>
      </Grow>

      {/* Invoice Dialog */}
      <InvoiceDialog
        open={invoiceDialogOpen}
        onClose={() => {
          setInvoiceDialogOpen(false)
          setInvoiceDialogData(null)
        }}
        invoiceData={invoiceDialogData}
        loading={loadingInvoice}
        readOnly={invoiceDialogReadOnly}
        onGenerateInvoice={
          invoiceDialogReadOnly ? undefined : handleGenerateInvoice
        }
      />

      {/* Claim Dialog */}
      <ClaimDialog
        open={claimDialogOpen}
        onClose={() => {
          setClaimDialogOpen(false)
          setClaimDialogData(null)
        }}
        claimData={claimDialogData}
        loading={loadingClaim}
        readOnly={true}
      />

      {/* Create Payment Batch Dialog */}
      <CreatePaymentBatchModal
        open={createBatchOpen}
        onClose={() => setCreateBatchOpen(false)}
        selectedInvoices={selectedRows}
        onCreate={handleCreateBatch}
        error={error}
      />
    </Box>
  )
}

export default AgencyInvoicePage
